import { BundleBase } from '@scale/bundle-base';
import { Events, QuantityBreaksUpdatedEvent } from '@scale/events';
import { formatMoney, shopCurrencyAmountToPresentment } from '@scale/currency';
import { buildBundleProperties, calculateBogo, calculateBogoFromPrices, calculateDiscount } from '@scale/bundle-utils';

/**
 * <scale-quantity-breaks> — container for quantity-break tiers.
 * Contributes to the parent <product-form> via getCartContribution().
 */
class QuantityBreaks extends BundleBase {
  setupBundle() {
    this.#selectDefault();
    for (const tier of this.#tiers()) this.#recomputeTierPrice(tier);
    this.on('change', this.#onChange.bind(this));
    this.onDocument(Events.VARIANT_SELECTED, this.#onProductVariantSelected.bind(this));
  }

  // --- Public API for product-form ---

  getBundleType() { return 'quantity_break'; }

  getBundleName() {
    const tier = this.#selectedTier();
    return tier ? `${tier.dataset.quantity} units` : 'Bundle';
  }

  async prepareCartContribution() {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const tier = this.#selectedTier();
      const offerId = tier?.dataset.offerId;
      await this.authorizeOffer(offerId);
      if (tier === this.#selectedTier() && offerId === this.#selectedTier()?.dataset.offerId) return;
    }
    throw new Error('Your bundle selection kept changing. Please review it and try again.');
  }

  getContributionTotal() {
    const tier = this.#selectedTier();
    if (!tier) return null;
    const total = Number(tier.dataset.tierTotal) || 0;
    const compare = Number(tier.dataset.compareTotal) || 0;
    return { replace: true, total, compare };
  }

  getCartContribution() {
    const tier = this.#selectedTier();
    if (!tier) return { replace: false, items: [] };

    const bundleProps = this.#tierBundleProps(tier);
    const items = [];

    const variantSelects = this.#variantSelectsIn(tier);
    if (variantSelects.length > 0) {
      variantSelects.forEach((select, i) => {
        items.push({
          id: Number(select.value),
          quantity: 1,
          properties: { ...bundleProps, _bundle_position: String(i + 1) },
        });
      });
    } else {
      items.push({
        id: Number(tier.dataset.variantId),
        quantity: Number(tier.dataset.quantity) || 1,
        properties: { ...bundleProps, _bundle_position: '1' },
      });
    }

    for (const cs of tier.querySelectorAll('[data-quantity-break-cross-sell]')) {
      const checkbox = cs.querySelector('[data-ref="quantity-break-cross-sell-checkbox"]');
      if (!checkbox?.checked) continue;
      items.push({
        id: Number(cs.dataset.variantId),
        quantity: 1,
        properties: { ...bundleProps, _bundle_addon: 'true' },
      });
    }

    const gift = tier.querySelector('[data-quantity-break-gift]');
    if (gift) {
      if (!this.authorizedGiftVariantId) {
        throw new Error('The selected bundle gift was not verified. Please try again.');
      }
      items.push({
        id: Number(this.authorizedGiftVariantId.split('/').pop()),
        quantity: 1,
        properties: { ...bundleProps, _bundle_gift: 'true' },
      });
    }

    return { replace: true, items };
  }

  #tierBundleProps(tier) {
    const quantity = tier.dataset.quantity;
    if (tier.dataset.offerId !== this.authorizedOfferId) {
      throw new Error('The selected bundle offer was not verified. Please try again.');
    }
    return buildBundleProperties({
      type: 'quantity_break',
      sectionId: this.sectionId,
      name: `${quantity}x`,
      offerId: tier.dataset.offerId,
      bundleId: this.bundleId,
      proof: this.bundleProof,
      proofDay: this.bundleProofDay,
    });
  }

  // --- Tier selection ---

  #tiers() {
    return [...this.querySelectorAll('[data-quantity-break]')];
  }

  #selectedTier() {
    return this.#tiers().find((t) => t.querySelector('[data-ref="quantity-break-radio"]')?.checked) ?? null;
  }

  #selectDefault() {
    const tiers = this.#tiers().filter((t) => !t.classList.contains('quantity-break--disabled'));
    if (!tiers.length) return;

    let target;
    if (this.getAttribute('data-auto-best-value') === 'true') {
      target = tiers.reduce((best, tier) => {
        const qty = Number(tier.dataset.quantity) || 1;
        const unit = (Number(tier.dataset.tierTotal) || 0) / qty;
        return !best || unit < best.unit ? { tier, unit } : best;
      }, null).tier;
    } else {
      const idx = Math.max(0, (Number(this.getAttribute('data-default-index')) || 1) - 1);
      target = tiers[Math.min(idx, tiers.length - 1)];
    }

    const radio = target.querySelector('[data-ref="quantity-break-radio"]');
    if (radio) {
      radio.checked = true;
      target.dataset.selected = 'true';
    }
  }

  #onChange(event) {
    const target = event.target;

    if (target.closest?.('[data-ref="quantity-break-radio"]')) {
      this.#selectTier(target);
      return;
    }

    const select = target.closest?.('[data-ref="quantity-break-variant-select"]');
    if (select) this.#onVariantSelectChange(select);
  }

  #selectTier(radio) {
    for (const tier of this.#tiers()) {
      const isSelected = tier.contains(radio);
      tier.dataset.selected = String(isSelected);
      if (!isSelected) {
        for (const cb of tier.querySelectorAll('[data-ref="quantity-break-cross-sell-checkbox"]')) {
          cb.checked = false;
        }
      }
    }

    this.broadcast(new QuantityBreaksUpdatedEvent(this.#selectedTier()?.dataset.variantId, this));
  }

  // --- Variant picker per unit ---

  #variantSelectsIn(tier) {
    // The child Theme Block cannot read its parent's settings directly. The
    // container is the authority: hidden per-unit controls must not silently
    // split one offer into N cart lines when composition is disabled.
    if (this.dataset.showVariantPicker !== 'true') return [];
    return [...tier.querySelectorAll('[data-ref="quantity-break-variant-select"]')];
  }

  #onVariantSelectChange(select) {
    const tier = select.closest('[data-quantity-break]');
    if (!tier) return;

    const row = select.closest('.quantity-break__variant-row');
    const thumbImg = row?.querySelector('.quantity-break__variant-thumb img');
    const src = select.options[select.selectedIndex]?.dataset.image;
    if (thumbImg && src) thumbImg.src = src;

    this.#recomputeTierFromVariants(tier);
    this.broadcast(new QuantityBreaksUpdatedEvent(tier.dataset.variantId, this));
  }

  #recomputeTierFromVariants(tier) {
    const selects = this.#variantSelectsIn(tier);
    if (!selects.length) return;

    let base = 0;
    let compare = 0;
    const prices = [];
    for (const select of selects) {
      const opt = select.options[select.selectedIndex];
      const price = Number(opt?.dataset.price) || 0;
      base += price;
      prices.push(price);
      compare += Number(opt?.dataset.comparePrice) || price;
    }

    const total = tier.dataset.bogoBuy
      ? calculateBogoFromPrices(prices, tier.dataset.bogoGet, tier.dataset.bogoDiscount)
      : this.#applyTierDiscount(tier, base);
    this.#renderPrices(tier, total, compare);
  }

  #recomputeTierPrice(tier) {
    if (this.#variantSelectsIn(tier).length > 0) {
      this.#recomputeTierFromVariants(tier);
      return;
    }
    const base = Number(tier.dataset.baseTotal) || 0;
    const compare = Number(tier.dataset.compareTotal) || base;
    this.#renderPrices(tier, this.#applyTierDiscount(tier, base), compare);
  }

  // --- Main product variant change ---

  #onProductVariantSelected(event) {
    const variant = event.variant;
    if (!variant) return;

    for (const tier of this.#tiers()) this.#updateTierForVariant(tier, variant);

    this.broadcast(new QuantityBreaksUpdatedEvent(variant.id, this));
  }

  #updateTierForVariant(tier, variant) {
    tier.dataset.variantId = variant.id;
    tier.dataset.unitPrice = variant.price;

    const qty = Number(tier.dataset.quantity) || 1;
    const base = variant.price * qty;
    const compare = variant.compare_at_price > variant.price ? variant.compare_at_price * qty : base;

    tier.dataset.baseTotal = base;
    tier.dataset.compareTotal = compare;

    this.#renderPrices(tier, this.#applyTierDiscount(tier, base), compare);
    this.#updateDisabled(tier, variant, qty);
  }

  #updateDisabled(tier, variant, qty) {
    const ok = variant.inventory_policy === 'continue'
      || !variant.inventory_management
      || (variant.available && variant.inventory_quantity >= qty);

    tier.classList.toggle('quantity-break--disabled', !ok);
    const radio = tier.querySelector('[data-ref="quantity-break-radio"]');
    if (radio) radio.disabled = !ok;

    if (!ok && tier.dataset.selected === 'true') {
      const replacement = this.#tiers().find((t) => !t.classList.contains('quantity-break--disabled'));
      const nextRadio = replacement?.querySelector('[data-ref="quantity-break-radio"]');
      if (nextRadio) {
        nextRadio.checked = true;
        nextRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  // --- Price math ---

  #applyTierDiscount(tier, baseTotal) {
    if (tier.dataset.bogoBuy) {
      return calculateBogo(
        Number(tier.dataset.unitPrice) || 0,
        Number(tier.dataset.bogoBuy) || 0,
        Number(tier.dataset.bogoGet) || 0,
        Number(tier.dataset.bogoDiscount) || 0,
      );
    }

    const type = tier.dataset.discountType;
    switch (type) {
      case 'percentage': return calculateDiscount(baseTotal, 'percentage', Number(tier.dataset.discountPercentage) || 0);
      case 'fixed_amount': {
        const amount = shopCurrencyAmountToPresentment(tier.dataset.discountValue);
        return amount === null ? baseTotal : calculateDiscount(baseTotal, 'fixed_amount', amount);
      }
      case 'flat_price': {
        const qty = Number(tier.dataset.quantity) || 1;
        const unitPrice = shopCurrencyAmountToPresentment(tier.dataset.overridePrice);
        return unitPrice === null ? baseTotal : Math.round(unitPrice * 100) * qty;
      }
      default: return baseTotal;
    }
  }

  #renderPrices(tier, total, compare) {
    tier.dataset.tierTotal = total;

    const priceEl = tier.querySelector('[data-ref="quantity-break-price"]');
    if (priceEl) priceEl.textContent = formatMoney(total);

    const compareEl = tier.querySelector('[data-ref="quantity-break-compare"]');
    const hasCompare = compare > total;
    if (compareEl) {
      compareEl.textContent = formatMoney(compare);
      compareEl.hidden = !hasCompare;
    } else if (hasCompare) {
      const s = document.createElement('s');
      s.className = 'quantity-break__compare';
      s.dataset.ref = 'quantity-break-compare';
      s.textContent = formatMoney(compare);
      tier.querySelector('.quantity-break__pricing')?.prepend(s);
    }

    const unitEl = tier.querySelector('[data-ref="quantity-break-unit"]');
    if (unitEl) {
      const qty = Number(tier.dataset.quantity) || 1;
      unitEl.textContent = `${formatMoney(total / qty)} ${unitEl.dataset.unitPriceText}`.trim();
    }
  }
}

if (!customElements.get('scale-quantity-breaks')) {
  customElements.define('scale-quantity-breaks', QuantityBreaks);
}
